import { numeralFormatter } from 'ns_libs/formatter';
import { AREA_PRECISION, NO_VALUE, PERCENTAGE_PRECISION } from '../../constants';
import { calculateDetailParkingMetrics, formatEfficiencyRatio, formatProFormaAddress } from '../../helpers';
import { CardItemValue, CardResolver } from '../../layout/cardTypes';
import { DetailsMetricId } from '../../layout/metrics';
import { ProFormaSummaryData } from '../../types';

const value = (displayed: string | number) => (): CardItemValue => ({ value: displayed });
const omitted = () => (): CardItemValue | null => null;

export const proFormaDetailsResolver = ({ proForma, incomeSummary, buildableLots }: ProFormaSummaryData): CardResolver<DetailsMetricId> => {
    const totalUnits = incomeSummary?.totalUnits || 0;
    const totalRentableSf = incomeSummary?.totalRentableSf || 0;
    const totalGrossBuiltSf = proForma?.builtGrossSquareFootage ? Number(proForma.builtGrossSquareFootage) : 0;
    const efficiencyRatio = formatEfficiencyRatio(totalRentableSf, totalGrossBuiltSf);

    const address = formatProFormaAddress(proForma);
    const description = proForma?.description || '';

    const {
        totalSpots: parkingSpaces, ratio: parkingRatio, suffix: parkingSuffix,
    } = calculateDetailParkingMetrics(proForma, buildableLots, totalUnits);

    const hasCoordinates = !!proForma?.useCoordinates && proForma?.latitude != null && proForma?.longitude != null;

    return {
        hasData: () => true,

        address: address && !proForma?.useCoordinates ? value(address) : omitted(),
        coordinates: hasCoordinates ? value(`${proForma.latitude}, ${proForma.longitude}`) : omitted(),

        landArea: value(`${numeralFormatter(buildableLots.totalLandAreaSquareFootage, AREA_PRECISION)} SF`),
        far: value(buildableLots.totalFar ? numeralFormatter(buildableLots.totalFar, PERCENTAGE_PRECISION) : NO_VALUE),
        grossBuildableArea: value(`${numeralFormatter(buildableLots.totalGrossBuildableArea, AREA_PRECISION)} SF`),
        buildings: value(numeralFormatter(proForma?.buildings || 0)),
        stories: value(numeralFormatter(proForma?.averageStories || 0)),
        units: value(numeralFormatter(totalUnits)),
        grossBuiltSf: value(`${numeralFormatter(totalGrossBuiltSf)} GSF`),
        netRentableArea: value(`${numeralFormatter(totalRentableSf)} RSF`),
        efficiencyRatio: value(efficiencyRatio ? `${efficiencyRatio}%` : NO_VALUE),
        parkingSpaces: value(numeralFormatter(parkingSpaces)),
        parkingRatio: value(parkingRatio ? `${parkingRatio} ${parkingSuffix}` : 'N/A'),
        description: description.trim() ? value(description) : omitted(),
    };
};
